/**
 * TimesTab Component
 * Displays time statistics and comparisons
 */
import React from 'react';
import { ResultData } from '../../types/results';
import { formatTime, calculateAverage } from '../../utils/formatters';

interface TimesTabProps {
  results: ResultData;
}

export const TimesTab: React.FC<TimesTabProps> = ({ results }) => {
  // Calculate averages
  const avgInstructionTime = results.instruction_times.length > 0 
    ? Math.round(results.total_instruction_time / results.instruction_times.length)
    : 0;

  const preRuleAnswers = results.answers.filter(a => a.pre_rule_time > 0);
  const avgPreRuleTime = preRuleAnswers.length > 0
    ? Math.round(results.total_pre_rule_time / preRuleAnswers.length)
    : 0;

  const distractionAnswers = results.answers.filter(a => a.distraction_time > 0);
  const avgDistractionTime = distractionAnswers.length > 0
    ? Math.round(results.total_distraction_time / distractionAnswers.length)
    : 0;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-gray-800">Statistiques temps</h2>
        <p className="text-sm text-gray-500">Moyennes temporelles</p>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Moyenne/question"
          value={`${results.average_answer_time}s`}
          bgColor="bg-primary bg-opacity-10"
          textColor="text-primary"
        />
        <StatCard
          label="Moyenne consignes"
          value={`${avgInstructionTime}s`}
          bgColor="bg-secondary bg-opacity-10"
          textColor="text-secondary"
        />
        <StatCard
          label="Durée totale"
          value={results.total_time_formatted}
          bgColor="bg-green-50"
          textColor="text-green-600"
        />
        <StatCard
          label="Moyenne distraction"
          value={`${avgDistractionTime}s`}
          bgColor="bg-yellow-50"
          textColor="text-yellow-600"
        />
      </div>

      {/* Comparison Table */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-sm text-gray-700 mb-3">Comparaison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Categorie</th>
                <th className="text-right py-2">Moyenne</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              <ComparisonRow
                category="Réponses"
                average={`${results.average_answer_time}s`}
                total={formatTime(results.total_answer_time)}
              />
              <ComparisonRow
                category="Consignes"
                average={`${avgInstructionTime}s`}
                total={formatTime(results.total_instruction_time)}
              />
              <ComparisonRow
                category="Règles"
                average={`${avgPreRuleTime}s`}
                total={formatTime(results.total_pre_rule_time)}
              />
              <ComparisonRow
                category="Distraction"
                average={`${avgDistractionTime}s`}
                total={formatTime(results.total_distraction_time)}
                isLast
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Stat card component
interface StatCardProps {
  label: string;
  value: string;
  bgColor: string;
  textColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, bgColor, textColor }) => (
  <div className={`${bgColor} p-4 rounded-lg text-center`}>
    <p className="text-gray-600 text-xs mb-1">{label}</p>
    <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
  </div>
);

// Comparison row component
interface ComparisonRowProps {
  category: string;
  average: string;
  total: string;
  isLast?: boolean;
}

const ComparisonRow: React.FC<ComparisonRowProps> = ({ 
  category, 
  average, 
  total, 
  isLast = false 
}) => (
  <tr className={isLast ? '' : 'border-b'}>
    <td className="py-2">{category}</td>
    <td className="text-right font-medium">{average}</td>
    <td className="text-right text-gray-500">{total}</td>
  </tr>
);

export default TimesTab;
